import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Modal } from '../../../../../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../../../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../../../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { Alert } from '../../../../../../../../../../CommonComponents/OPL/Alert/Alert';
import { Button } from '../../../../../../../../../../CommonComponents/OPL/Button/Button';
import { ModalFooter } from '../../../../../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { validation } from '../../EditAssetForm.meta';
import { EditAssetForm } from '../../EditAssetForm';
import { useBeneficiaries } from '../../../../useBeneficiaries';
import { useEquityCompensationMutation } from '../../../../../../api/useEquityCompensationMutation';
import { EquityCompensationAssetDTO } from '../../../../../../api/useEstatePlanningExplanationQuery';
import {
  EQUITY_COMPENSATION_ASSET_FORM_ID,
  formToBody,
  FormValues,
} from './EquityCompensationEditAssetForm.meta';
import { toast } from '../../../../../../../../../../CommonComponents/OPL/Toast/Toast';
import { MESSAGES } from '../../../../../../../../../../CommonConstants';
import { Select } from '../../../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Box } from '../../../../../../../../../../CommonComponents/OPL/Box/Box';

interface Props {
  asset: EquityCompensationAssetDTO;
  onRequestClose: () => void;
}

export const EquityCompensationEditAssetForm = ({ asset, onRequestClose }: Props) => {
  const { data } = useBeneficiaries();

  const {
    mutateAsync: updateEquityCompensationBeneficiaries,
    error: updateEquityCompensationBeneficiariesError,
  } = useEquityCompensationMutation();

  if (!data) {
    return null;
  }

  const { ContingentBeneficiaries, PrimaryBeneficiaries, LastUpdated, ...rest } = asset;
  const { includedInEstatePlanOptions } = data;

  const formBag = useForm<FormValues>({
    defaultValues: {
      ...rest,
      PrimaryBeneficiaries: PrimaryBeneficiaries || [],
      ContingentBeneficiaries: ContingentBeneficiaries || [],
      LastUpdated,
    },
    resolver: yupResolver(validation),
  });

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    watch,
  } = formBag;

  const includedInEstatePlan = watch('IncludedInEstatePlan');

  const onSubmit = handleSubmit(async values => {
    const body = formToBody(values);

    try {
      await updateEquityCompensationBeneficiaries({ body });

      onRequestClose();
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error(MESSAGES.GENERIC_ERROR);
      }
      console.error(err);
    }
  });

  return (
    <Modal isOpen onRequestClose={onRequestClose} aria={{ labelledby: 'modal-header' }}>
      <ModalHeader as="h2" id="modal-header">
        Beneficiaries
      </ModalHeader>
      <ModalCloseButton onClick={onRequestClose} />

      <ModalBody css="padding: 0">
        <form onSubmit={onSubmit} id={EQUITY_COMPENSATION_ASSET_FORM_ID}>
          {updateEquityCompensationBeneficiariesError && (
            <Alert
              variant="error"
              message={updateEquityCompensationBeneficiariesError.message}
              m="24"
            />
          )}

          <Box px="16">
            <Controller
              control={control}
              name="IncludedInEstatePlan"
              render={({ field: { value, onChange } }) => (
                <Select
                  isPortal
                  label="Have you included this asset in your estate plan?"
                  mb="16"
                  value={includedInEstatePlanOptions.find(option => option.value === value)}
                  options={includedInEstatePlanOptions}
                  onChange={opt => {
                    if (opt) {
                      onChange(opt.value);
                    }
                  }}
                />
              )}
            />
          </Box>

          {includedInEstatePlan !== 3 && (
            <FormProvider {...formBag}>
              <EditAssetForm />
            </FormProvider>
          )}
        </form>
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" onClick={onRequestClose}>
          Cancel
        </Button>

        <Button
          type="submit"
          isLoading={isSubmitting}
          ml="8"
          form={EQUITY_COMPENSATION_ASSET_FORM_ID}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};
